/**
 * Service-worker registration and the install prompt.
 *
 * Both are small and both have one non-obvious rule, which is why they are here rather than inline.
 */

import { MOCKS_ENABLED } from '@dedupr/app-kit';

/**
 * Whether MSW is running.
 *
 * ⚠️ **Imported from `@dedupr/app-kit` rather than re-derived**, and that is the fix for a real bug:
 * a first version read `VITE_MOCKS`, which is not the variable this repo uses — the name is
 * `VITE_USE_MOCKS`. It therefore read `undefined` in every environment, concluded "mocks on", and
 * would have skipped service-worker registration **even with mocks explicitly off**. Two copies of
 * one predicate is exactly how that happens, so there is now one.
 */

/**
 * Register the offline shell.
 *
 * ⚠️ **Refused in mock mode and in dev, and neither is caution for its own sake.**
 *
 *   • **Mock mode**: MSW registers its own worker at the same scope, and the later registration wins
 *     — so ours would silently switch off request interception.
 *   • **Dev**: `public/` is served as-is by Vite, so `sw.js` is reachable; caching the dev shell
 *     means an edit stops appearing until the cache is cleared by hand, which reads as "HMR is
 *     broken" rather than as "a worker is serving you an old page".
 *
 * Returns what it did, so a caller (and a test) can assert the refusal rather than infer it.
 *
 * `options` exists for the tests: `MOCKS_ENABLED` is a module-level const evaluated at import, so a
 * test cannot change it — and mocking the whole `@dedupr/app-kit` module to fake it **leaked across
 * test files** and broke an unrelated search assertion, because that stub replaced `api` too. An
 * injected flag needs no module mocking at all.
 */
export async function registerServiceWorker(options: { mocksEnabled?: boolean; dev?: boolean } = {}): Promise<'registered' | 'skipped_mocks' | 'skipped_dev' | 'unsupported' | 'failed'> {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return 'unsupported';
  if (options.mocksEnabled ?? MOCKS_ENABLED) return 'skipped_mocks';
  if (options.dev ?? import.meta.env.DEV) return 'skipped_dev';

  try {
    await navigator.serviceWorker.register('/sw.js', { scope: '/' });
    return 'registered';
  } catch {
    /* Swallowed on purpose. A failed registration costs offline support and nothing else — the app
       works entirely without it — so surfacing it to a visitor would be noise about a capability
       they did not ask for. */
    return 'failed';
  }
}

/** The `beforeinstallprompt` event, which TypeScript's DOM lib does not declare. */
interface InstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

let deferred: InstallPromptEvent | null = null;

/**
 * Capture the install prompt so it can be offered at a moment that makes sense.
 *
 * ⚠️ `preventDefault()` is what makes this possible at all: without it the browser shows its own
 * prompt immediately, and the event cannot be replayed later. Chrome fires it once per page load, so
 * a prompt dismissed by the visitor is genuinely gone until they reload — which is why
 * `canInstall()` reports honestly rather than leaving a button that does nothing.
 */
export function captureInstallPrompt(): void {
  if (typeof window === 'undefined') return;
  window.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault();
    deferred = event as InstallPromptEvent;
  });
  // Once installed the event never fires again, so the captured one is stale and must be dropped.
  window.addEventListener('appinstalled', () => {
    deferred = null;
  });
}

export function canInstall(): boolean {
  return deferred !== null;
}

/** Show the browser's install prompt. Returns null when there is nothing to show. */
export async function promptInstall(): Promise<'accepted' | 'dismissed' | null> {
  if (!deferred) return null;
  const event = deferred;
  // Cleared before awaiting: the event is single-use, and a second `prompt()` on the same one throws.
  deferred = null;
  await event.prompt();
  const { outcome } = await event.userChoice;
  return outcome;
}

/**
 * Whether the app is running as an installed PWA.
 *
 * Two checks because the platforms differ: `display-mode: standalone` is the standard, and
 * `navigator.standalone` is what iOS Safari sets for a home-screen app.
 */
export function isInstalled(): boolean {
  if (typeof window === 'undefined') return false;
  if (window.matchMedia?.('(display-mode: standalone)').matches) return true;
  return (navigator as Navigator & { standalone?: boolean }).standalone === true;
}
